/*
 * Author: Dw
 * File Created: Monday, 25-01-13 03:37 pm
 * Last Modified: Monday, 25-04-21 03:33 pm, by Dw
 */

import type { GetServerSidePropsContext } from 'next';
import { NextRequest, NextResponse } from 'next/server';
import Cookies, { CookieSetOptions } from 'universal-cookie';

import { isClient } from './env';
import getEnv from './env';

export const getCookieDomain = (hostname = '') =>
  hostname.includes('localhost')
    ? 'localhost'
    : hostname
        .replace(/http(s)?:\/\//, '')
        .split(':')[0]
        ?.split('.')
        .slice(-2)
        .join('.') || '';

const getCookieInstance = (cookies?: object | string) => {
  return new Cookies(typeof window === 'undefined' ? cookies : undefined, {
    // default 365d
    maxAge: Math.floor(24 * 60 * 60 * 365),
    path: '/',
  });
};

const checkDomain = (hostname: string, fullDomain?: boolean) => {
  const cleanHostname = hostname.split(':')[0] ?? '';

  if (cleanHostname === 'localhost') {
    return 'localhost';
  }

  const isLocalIP =
    /^(127\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+|172\.(1[6-9]|2[0-9]|3[0-1])\.\d+\.\d+)$/.test(
      cleanHostname
    );

  if (isLocalIP) {
    return cleanHostname;
  }

  return fullDomain
    ? cleanHostname
    : cleanHostname.split('.').slice(-2).join('.') || undefined;
};

/**
 * cookie 查询
 * @param name cookie name
 * @param cookiesOrRequest 可选，cookies 对象或 NextRequest 实例
 * @returns string | null
 */
function get(name: string): string | null;
function get(name: string, cookies?: object | string): string | null;
function get(name: string, request: NextRequest): string | null;
function get(
  name: string,
  cookiesOrRequest?: object | string | NextRequest
): string | null {
  if (cookiesOrRequest instanceof NextRequest) {
    // 从 NextRequest 中获取 cookies
    return cookiesOrRequest.cookies.get(name)?.value || null;
  } else {
    if (!cookies) return null;

    // 使用 cookies 对象或字符串获取
    return getCookieInstance(cookiesOrRequest).get(name);
  }
}

type CookieOptions = Omit<CookieSetOptions, 'maxAge'> & {
  // in day unit
  maxAge?: number;
  cookies?: object;
  fullDomain?: boolean;
  noDefaultDomain?: boolean;
};

/**
 * handleCookie - ServerSideProps 重载
 */
function handleCookie(
  cookieObj: GetServerSidePropsContext['res'],
  config:
    | {
        key: string;
        value: string;
        request: GetServerSidePropsContext['req'];
        maxAge?: number;
        path?: string;
        fullDomain?: boolean;
        noDefaultDomain?: boolean;
      }
    | {
        // Support an array of cookie configurations
        key: string;
        value: string;
        request: GetServerSidePropsContext['req'];
        maxAge?: number;
        path?: string;
        fullDomain?: boolean;
        domain?: string;
        noDefaultDomain?: boolean;
      }[]
): void;

/**
 * handleCookie - Middleware 重载
 */
function handleCookie(
  cookieObj: NextResponse['cookies'],
  config: {
    key: string;
    value?: string;
    request: NextRequest;
    options?: Partial<ReturnType<NextResponse['cookies']['get']>>;
    noDefaultDomain?: boolean;
    fullDomain?: boolean;
    domain?: string;
  }
): void;

/**
 * handleCookie - 通用重载
 */
function handleCookie(name: string, value: any, options?: CookieOptions): void;

function handleCookie(arg1: any, arg2: any, arg3?: any): void {
  if (typeof arg1 === 'string') {
    // 通用逻辑
    const name: string = arg1;
    const value: any = arg2;
    const options: CookieOptions | undefined = arg3;

    const { noDefaultDomain, fullDomain, ...setOptions } = { ...options };

    // maxAge 转换为秒
    if (options?.maxAge) {
      setOptions.maxAge = Math.floor(options.maxAge * 24 * 60 * 60);
    }

    // 设置默认 domain
    if (!options?.domain) {
      const hostname = isClient ? window.location.hostname : '';
      setOptions.domain = noDefaultDomain
        ? ''
        : checkDomain(hostname, fullDomain);
    }

    getCookieInstance(options?.cookies).set(name, value, setOptions);
  } else if ('setHeader' in arg1) {
    // ServerSideProps 的 res.setHeader
    const cookieObj: GetServerSidePropsContext['res'] = arg1;
    const configs = Array.isArray(arg2) ? arg2 : [arg2];

    // Create an array to collect multiple Set-Cookie headers
    const cookieString = configs.map((config) => {
      const { key, value, maxAge, request, path, fullDomain, noDefaultDomain } =
        config;
      const hostname = request.headers.host;
      const cookieDomain = checkDomain(hostname, fullDomain);

      // Construct the cookie string
      let cookieStr = `${key}=${value}; Path=${path ?? '/'}; Max-Age=${maxAge || ''};`;
      if (!noDefaultDomain) {
        cookieStr = cookieStr.concat(` Domain=${cookieDomain}`);
      }

      return cookieStr;
    });

    cookieObj.setHeader('Set-Cookie', cookieString);
  } else {
    // Middleware 的 NextResponse.cookies
    const cookieObj: NextResponse['cookies'] = arg1;
    const config = arg2;
    const {
      key,
      value = '',
      options = {},
      request,
      noDefaultDomain,
      fullDomain,
    } = config;

    if (options.domain === undefined) {
      const isLocalhost = request?.nextUrl.hostname === 'localhost';
      const _hostName = new URL(
        process.env.NEXT_PUBLIC_SITE_HOST ?? request?.url ?? ''
      ).hostname;
      const targetDomain = isLocalhost
        ? 'localhost'
        : fullDomain
          ? _hostName
          : _hostName.split('.').slice(-2).join('.');

      options.domain = noDefaultDomain ? '' : targetDomain;
      if (getEnv() === 'local') {
        // for fix safari local env can not access the cookie
        // options.secure = false;
      }
    }

    if (!options.domain) {
      delete options.domain;
    }

    cookieObj.set(key, value, options);
  }
}

/**
 * 删除 cookie - ServerSideProps 重载
 */
function clearCookie(
  cookieObj: GetServerSidePropsContext['res'],
  config: {
    key: string;
    request: NextRequest;
  }
): void;

/**
 * 删除 cookie - Middleware 重载
 */
function clearCookie(
  cookieObj: NextResponse['cookies'],
  config: {
    key: string;
    request: NextRequest;
    noDefaultDomain?: boolean;
    options?: Partial<ReturnType<NextResponse['cookies']['get']>>;
  }
): void;

/**
 * 删除 cookie - 通用重载
 */
function clearCookie(name: string, options?: CookieOptions): void;

function clearCookie(arg1: any, arg2?: any): void {
  if (typeof arg1 === 'string') {
    // 通用逻辑
    const name: string = arg1;
    const options: CookieOptions | undefined = arg2;

    handleCookie(name, '', {
      ...options,
      maxAge: -1, // 设置过期时间为负数以立即删除
    });
  } else if ('setHeader' in arg1) {
    // ServerSideProps 的 res.setHeader 删除逻辑
    const cookieObj: GetServerSidePropsContext['res'] = arg1;
    const config = arg2;

    const { key, request } = config;
    const hostname = request?.headers.get('host') || '';
    const isLocalhost = hostname.includes('localhost');
    const cookieDomain = isLocalhost
      ? 'localhost'
      : hostname.split(':')[0]?.split('.').slice(-2).join('.');

    const cookieString = `${key}=; Path=/; Max-Age=0; Domain=${cookieDomain}`;
    cookieObj.setHeader('Set-Cookie', cookieString);
  } else {
    // Middleware 的 NextResponse.cookies 删除逻辑
    const cookieObj: NextResponse['cookies'] = arg1;
    const config = arg2;

    handleCookie(cookieObj, {
      ...config,
      value: '',
      options: {
        ...config.options,
        secure: getEnv() !== 'local',
        expires: new Date(0),
        domain: config.noDefaultDomain ? '' : config.options?.domain,
      },
    });
  }
}

export const cookies = {
  get,
  set: handleCookie,
  clear: clearCookie,
};
